// Nearby Tab Container
//
// Single-card vertical scroll view of people close to where the viewer
// actually is. Previously this rendered a hardcoded list of invented users.
//
// Location comes from the browser and is sent with each request, because
// signing in from another city should change the feed. A denied permission is
// not fatal: the request omits the coordinates and the backend falls back to
// the stored profile location (get_nearby_candidates, migration 044).
//
// Each card plays that profile's intro video, and only the card on screen
// plays — a dozen simultaneous videos would compete for bandwidth and decode
// while the viewer can watch exactly one.

import React, { useState, useEffect, useCallback, useRef } from "react";
import { FeedItem } from "../../../types/socialFeed";
import { swipeService } from "../../../services/Swipe/swipeService";
import {
  nearbyService,
  getCurrentPosition,
  newNearbySeed,
  NearbyPosition,
} from "../../../services/SocialFeed/nearbyService";
import {
  Looped,
  LoopCursor,
  startLoop,
  tagCycle,
  appendLooped,
  advanceCursor,
  poolIsEmpty,
} from "../../../services/SocialFeed/feedLoop";
import {
  feedCacheKey,
  readAnyFeedCache,
  writeFeedCache,
} from "../../../services/SocialFeed/feedCache";
import { nearbyVariant } from "../../../services/SocialFeed/feedPrefetch";
import { logger } from "../../../utils/logger";
import { MatchCard } from "../FeedItems/MatchCard";
import { ProfilePeekDrawer, useProfilePeek } from "../../shared/ProfilePeekDrawer";
import { signalsService } from "../../../services/SocialFeed/signalsService";
import {
  MatchesContainer,
  MatchesScrollContainer,
  LoadingContainer,
  LoadingSpinner,
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateDescription,
  FeedToast,
} from "../../../styles/SocialFeed/MatchesTab.styles";

interface NearbyTabContainerProps {
  userId: string;
}

const PAGE_SIZE = 20;
// Fetch the next page a few cards before the end, so the feed never stalls at
// the bottom while a request is in flight.
const PREFETCH_CARDS_FROM_END = 3;

export const NearbyTabContainer: React.FC<NearbyTabContainerProps> = ({
  userId,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Nearby's cache key depends on the device position, which is not known
  // until the browser answers. So the newest entry for this viewer is taken
  // whatever position produced it, shown straight away, and corrected below if
  // the resolved position turns out to belong to a different list.
  const [cached] = useState(() => readAnyFeedCache<Looped<FeedItem>>('nearby', userId));

  const [items, setItems] = useState<Looped<FeedItem>[]>(cached?.entry.items ?? []);
  const [isLoading, setIsLoading] = useState(!cached);
  const [deckCount, setDeckCount] = useState(0);
  const [usersInDeck, setUsersInDeck] = useState<Set<string>>(new Set());
  const [likedUsers, setLikedUsers] = useState<Set<string>>(new Set());
  // Passed this session, so the X can show red here too — the Nearby tab
  // renders the same card and had the same silent button.
  const [passedUsers, setPassedUsers] = useState<Set<string>>(new Set());
  const [currentCardIndex, setCurrentCardIndex] = useState(0);

  const [loadError, setLoadError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [usingProfileLocation, setUsingProfileLocation] = useState(false);
  // Shared with the other feeds; also emits profile_open and profile_dwell.
  const { peekTarget, openPeek, closePeek, toast, showToast } = useProfilePeek('nearby');

  // Resolved once per mount and reused for paging, so every page is measured
  // from the same origin — re-reading the position between pages would reorder
  // the feed underneath the viewer.
  const positionRef = useRef<NearbyPosition | null>(null);
  // Offset, lap and seed. The seed only ever changes when a pass ends, so
  // paging stays stable within a pass and the next lap comes back reordered.
  const cursorRef = useRef<LoopCursor>(cached?.entry.cursor ?? startLoop(newNearbySeed()));
  /** The variant the rendered list came from, so a stale one can be spotted. */
  const loadedKeyRef = useRef<string | null>(cached?.key ?? null);
  const isFetchingRef = useRef(false);

  /**
   * @param replace  Swap the list rather than extend it, and keep the spinner
   *                 down. Used when a resolved device position turns out to
   *                 produce a different list from the cached one: there is
   *                 already content on screen, so a spinner would be a step
   *                 backwards.
   */
  const loadPage = useCallback(async ({ replace = false }: { replace?: boolean } = {}) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    if (!replace) setIsLoading(true);

    const cursor = cursorRef.current;

    try {
      const page = await nearbyService.getNearbyFeed({
        position: positionRef.current,
        limit: PAGE_SIZE,
        offset: cursor.offset,
        seed: cursor.seed,
      });
      setLoadError(null);

      // Out of people nearby means back to the top with a new ordering, not a
      // dead end. There is no third state.
      cursorRef.current = advanceCursor(cursor, page.length, PAGE_SIZE, newNearbySeed);

      if (poolIsEmpty(cursor, page.length)) setHasMore(false);

      const tagged = tagCycle(page, cursor.cycle);
      setItems((prev) => (replace ? tagged : appendLooped(prev, tagged)));
    } catch (error: any) {
      logger.error("[NEARBY] Failed to load nearby feed:", error);
      setLoadError(error?.message || "Nearby could not be loaded. Try again shortly.");
      setHasMore(false);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, []);

  // With nothing cached this waits for the position before loading, so the
  // first page is centred correctly rather than being drawn from the profile
  // address and visibly replaced a moment later.
  //
  // With something cached it is already on screen, and the position only
  // decides whether that list needs replacing.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const position = await getCurrentPosition();
      if (cancelled) return;
      positionRef.current = position;
      setUsingProfileLocation(!position);

      const key = feedCacheKey('nearby', userId, nearbyVariant(position));
      // Showing a list built from the right position already.
      if (loadedKeyRef.current === key) return;

      loadedKeyRef.current = key;
      // Restart the loop: these are different people in a different order, so
      // continuing the old cursor would page into the wrong list.
      cursorRef.current = startLoop(newNearbySeed());
      loadPage({ replace: true });
    })();

    return () => { cancelled = true; };
  }, [loadPage, userId]);

  // Hand the list back for the next visit, under the key it was built from.
  useEffect(() => {
    if (items.length && loadedKeyRef.current) {
      writeFeedCache(loadedKeyRef.current, {
        items,
        cursor: cursorRef.current,
        meta: { usingProfileLocation },
      });
    }
  }, [items, usingProfileLocation]);

  // Load deck count and check which users are in deck
  useEffect(() => {
    const loadDeckInfo = async () => {
      try {
        const count = await swipeService.getDeckCount(userId);
        setDeckCount(count);

        // Check which items are already in deck
        const inDeckSet = new Set<string>();
        for (const item of items) {
          const isInDeck = await swipeService.isInDeck(userId, item.userId);
          if (isInDeck) {
            inDeckSet.add(item.userId);
          }
        }
        setUsersInDeck(inDeckSet);
      } catch (error) {
        console.error("Failed to load deck info:", error);
      }
    };

    if (items.length > 0) {
      loadDeckInfo();
    }
  }, [userId, items]);

  // recordSwipe takes the *user* being swiped on, not the feed item id — these
  // differ now that cards are real ("nearby:<uuid>" vs the uuid). Passing the
  // item id sent a malformed target and the swipe never registered; it only
  // ever worked because the old mock items used the user id as their id.
  // Drop someone from the deck locally after a swipe.
  //
  // No API call: recordSwipe already deletes the swipe_deck_queue row for the
  // profile it acted on, whatever the action was. Only this component's copy
  // of that state was left behind — which is why the card icon kept showing a
  // green tick for someone the server had already removed.
  // Restore the like/pass buttons from the server, the same way the deck icon
  // above already restores itself. Without this the heart and X were session
  // state: a reload wiped them, and any profile that can legitimately come
  // back — one re-added to the deck queue, or opened directly — returned with
  // its buttons blank despite the swipe being on record.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    swipeService.getSwipedProfileIds()
      .then(({ liked, passed }) => {
        if (cancelled) return;
        // Seeded, not overwritten: anything swiped since mount is already in
        // these sets and must not be dropped by a slow response landing late.
        // (forEach rather than spreading Sets: this tsconfig targets ES5.)
        setLikedUsers((prev) => {
          const next = new Set(prev);
          liked.forEach((id) => next.add(id));
          return next;
        });
        setPassedUsers((prev) => {
          const next = new Set(prev);
          passed.forEach((id) => next.add(id));
          return next;
        });
      })
      .catch((error) => console.error("Failed to restore swipe state:", error));

    return () => { cancelled = true; };
  }, [userId]);

  const dropFromDeckLocally = useCallback((targetUserId: string) => {
    setUsersInDeck((prev) => {
      if (!prev.has(targetUserId)) return prev;
      const next = new Set(prev);
      next.delete(targetUserId);
      return next;
    });
  }, []);

  // The count follows the set, in one place, rather than being re-derived at
  // every call site that changes membership — which is how the two drift.
  useEffect(() => {
    setDeckCount(usersInDeck.size);
  }, [usersInDeck]);

  const handleLike = useCallback(
    async (targetUserId: string) => {
      try {
        await swipeService.recordSwipe(userId, targetUserId, 'like');
        // The heart is an up vote: interest in this profile.
        signalsService.track({ signalType: 'profile_like', targetUserId, surface: 'nearby' });
        setLikedUsers((prev) => new Set(prev).add(targetUserId));
        // recordSwipe unqueues on ANY action, so a like leaves the deck just
        // as a pass does. Reflected here so the icon does not claim otherwise.
        dropFromDeckLocally(targetUserId);
        // Liking undoes a pass. The two are opposite answers to the same
        // question, and the swipes table agrees — record_swipe_and_check_match
        // upserts on (swiper_id, swiped_id), so the row holds the LATEST
        // choice, never both. The UI has to say the same thing: pass then
        // like used to leave a red X sitting next to a filled heart.
        setPassedUsers((prev) => {
          if (!prev.has(targetUserId)) return prev;
          const next = new Set(prev);
          next.delete(targetUserId);
          return next;
        });
      } catch (error: any) {
        logger.error("[NEARBY] Failed to like user:", error);
        showToast(error?.code === 'SWIPE_LIMIT_REACHED'
          ? error.message
          : "Couldn't record that like. Please try again.");
      }
    },
    [userId, showToast, dropFromDeckLocally]
  );

  const handlePass = useCallback(
    async (targetUserId: string) => {
      try {
        await swipeService.recordSwipe(userId, targetUserId, 'pass');
        // The X is a down vote: an explicit loss of interest.
        signalsService.track({ signalType: 'profile_pass', targetUserId, surface: 'nearby' });
        // After the write, not before: red means recorded.
        setPassedUsers((prev) => new Set(prev).add(targetUserId));
        // Passing on someone and keeping them queued in your deck are
        // contradictory answers, and the server has already unqueued them.
        dropFromDeckLocally(targetUserId);
        setLikedUsers((prev) => {
          const newSet = new Set(prev);
          newSet.delete(targetUserId);
          return newSet;
        });
      } catch (error: any) {
        logger.error("[NEARBY] Failed to pass user:", error);
        showToast(error?.code === 'SWIPE_LIMIT_REACHED'
          ? error.message
          : "Couldn't record that pass. Please try again.");
      }
    },
    [userId, showToast, dropFromDeckLocally]
  );

  // Handle adding/removing user from deck (renamed from handleToggleDeck)
  const handleSaveToDeck = useCallback(
    async (targetUserId: string, isCurrentlySaved: boolean) => {
      try {
        if (isCurrentlySaved) {
          // Remove from deck
          await swipeService.removeFromDeck(userId, targetUserId);
          signalsService.track({ signalType: 'deck_remove', targetUserId, surface: 'nearby' });
          setUsersInDeck((prev) => {
            const newSet = new Set(prev);
            newSet.delete(targetUserId);
            return newSet;
          });
        } else {
          // Add to deck
          await swipeService.addToDeck(userId, targetUserId);
          // The card icon is the quick path to the same Add to Deck the
          // profile offers, so it records the same signal.
          signalsService.track({ signalType: 'deck_add', targetUserId, surface: 'nearby' });
          setUsersInDeck((prev) => new Set(prev).add(targetUserId));
        }
        const newCount = await swipeService.getDeckCount(userId);
        setDeckCount(newCount);
      } catch (error) {
        // Deck writes throw on server rejection now; the card calls this
        // without awaiting, so rethrowing would only produce an unhandled
        // rejection. Leave the button reflecting the unchanged server state.
        console.error("Failed to toggle deck:", error);
      }
    },
    [userId]
  );

  // Handle viewing profile
  // Slides the profile in over the feed rather than navigating away, matching
  // the Matches tab. Navigating cost the viewer their scroll position and the
  // playing video, and made getting back a browser-history question.
  const handleViewProfile = useCallback(
    (profileId: string) => {
      const item = items.find((i) => i.userId === profileId);
      if (!item) return;
      const distance = (item.metadata as any)?.distanceMiles;
      openPeek({
        profileId,
        firstName: item.username || 'Someone',
        age: (item.metadata as any)?.age,
        compatibilityScore: item.content?.compatibilityScore,
        // Nearby's context is proximity, where Matches uses compatibility.
        context:
          typeof distance === 'number'
            ? `${distance < 1 ? 'Less than a mile' : `${Math.round(distance)} miles`} away`
            : 'Near you',
      });
    },
    [items]
  );

  // Handle scroll
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const container = e.currentTarget;
      const { scrollTop, scrollHeight, clientHeight } = container;

      // Prevent division by zero
      const maxScroll = scrollHeight - clientHeight;
      if (maxScroll <= 0) return;

      // Calculate which card is currently visible based on scroll position
      // Each card is exactly clientHeight tall
      const cardHeight = clientHeight;
      const newIndex = Math.round(scrollTop / cardHeight);
      setCurrentCardIndex(newIndex);

      // Page in before the viewer reaches the end, rather than after.
      if (hasMore && !isFetchingRef.current && newIndex >= items.length - PREFETCH_CARDS_FROM_END) {
        loadPage();
      }
    },
    [hasMore, items.length, loadPage]
  );

  if (isLoading && items.length === 0) {
    return (
      <MatchesContainer>
        <LoadingContainer>
          <LoadingSpinner />
          <p>Loading nearby users...</p>
        </LoadingContainer>
      </MatchesContainer>
    );
  }

  if (loadError && items.length === 0) {
    return (
      <MatchesContainer>
        <EmptyState>
          <EmptyStateIcon>⚠️</EmptyStateIcon>
          <EmptyStateTitle>Couldn't load Nearby</EmptyStateTitle>
          <EmptyStateDescription>{loadError}</EmptyStateDescription>
        </EmptyState>
      </MatchesContainer>
    );
  }

  if (items.length === 0) {
    return (
      <MatchesContainer>
        <EmptyState>
          <EmptyStateIcon>📍</EmptyStateIcon>
          <EmptyStateTitle>No nearby users</EmptyStateTitle>
          <EmptyStateDescription>
            {usingProfileLocation
              ? "We're using your profile location because location access is off. Turn it on to see who's around you right now."
              : "Check back later to discover people near you."}
          </EmptyStateDescription>
        </EmptyState>
      </MatchesContainer>
    );
  }

  return (
    <>
    <MatchesContainer>
      {/* Scroll Container - One card at a time */}
      <MatchesScrollContainer
        ref={scrollContainerRef}
        onScroll={handleScroll}
      >
        {items.map((item, index) => (
          <MatchCard
            key={item.loopKey}
            item={item}
            onLike={handleLike}
            onPass={handlePass}
            onSaveToDeck={handleSaveToDeck}
            onViewProfile={handleViewProfile}
            isLiked={likedUsers.has(item.userId)}
            isPassed={passedUsers.has(item.userId)}
            isSaved={usersInDeck.has(item.userId)}
            // Only the card on screen plays. Neighbours are kept ready so a
            // scroll doesn't land on a blank frame while a video buffers.
            isActive={index === currentCardIndex}
            shouldPreload={Math.abs(index - currentCardIndex) <= 1}
            surface="nearby"
          />
        ))}
      </MatchesScrollContainer>
    </MatchesContainer>

    {toast && <FeedToast role="status">{toast}</FeedToast>}

    {/* Profile preview — the same drawer the Matches tab opens, so the profile
        icon behaves identically in both feeds. */}
    <ProfilePeekDrawer
      target={peekTarget}
      onClose={closePeek}
      backLabel="Nearby"
      onNotify={(_type, message) => showToast(message)}
      actions={peekTarget ? [
        // Same pair as Matches, and for the same reason no Deck action: the
        // profile body already carries its own Add to Deck button.
        {
          key: 'like',
          label: '♥ Like',
          variant: 'primary',
          onClick: () => handleLike(peekTarget.profileId),
        },
        {
          key: 'pass',
          label: '✕',
          variant: 'danger',
          ariaLabel: `Pass on ${peekTarget.firstName}`,
          onClick: () => handlePass(peekTarget.profileId),
        },
      ] : []}
    />
    </>
  );
};

